import { useCallback, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { TrayRefProps } from '@cbhq/cds-web/overlays/tray/Tray';
import { useToast } from '@cbhq/cds-web/overlays/useToast';
import { useRefreshWorkspaceMembers } from '@cbhq/identity-api-hooks/account-management/fetch';
import type { CoinbaseEngUsersAccountManagementApiApiLegalEntityRole } from '@cbhq/identity-api-hooks/account-management/types';

import { useOrganization } from '../../../contexts/OrganizationContext';
import { useStepUpSubmission } from '../../../hooks/useStepUpSubmission';
import type { EditMemberStatus } from '../components/EditMemberTray/EditMemberTrayHeader';
import { MemberRecentActivity } from '../components/EditMemberTray/MemberRecentActivity';
import { MultiLEProductAccessGroup } from '../components/InviteTray/MultiLEProductAccessGroup';
import { PRODUCTS } from '../components/InviteTray/productCatalog';
import { SaveFooter } from '../components/SaveFooter';
import { StepUpTwoFactorModal } from '../components/StepUpTwoFactorModal';
import { UnsavedChangesAlert } from '../components/UnsavedChangesAlert';
import { useEditSubmission } from '../useEditSubmission';
import { useMultiLEProductAccessEntities } from '../useMultiLEProductAccessEntities';
import type { EntityIdToProductSelections, ProductSelections } from '../useMultiLEProductSelection';
import { useMultiLEProductSelection } from '../useMultiLEProductSelection';
import type { Member as TeamMember, MemberRole, MemberRoleMap } from '../utils/constants';
import { isGatedRoleMutation } from '../utils/stepUp';
import { getProductKey, useRoles } from '../utils/useRoles';

import { EditMemberTray } from './EditMemberTray';

const messages = defineMessages({
  saveButton: {
    id: 'Team.multiLEEditTray.saveButton',
    defaultMessage: 'Save changes',
    description: 'Save button in the multi-LE edit member tray',
  },
  saveSuccess: {
    id: 'Team.multiLEEditTray.saveSuccess',
    defaultMessage: 'Membership updated',
    description: 'Toast shown after successfully saving member role changes',
  },
  saveError: {
    id: 'Team.multiLEEditTray.saveError',
    defaultMessage: 'Error updating membership',
    description: 'Toast shown when saving member role changes fails',
  },
});

type MultiLEEditTrayProps = {
  member: TeamMember;
  memberLegalEntityRoles?: CoinbaseEngUsersAccountManagementApiApiLegalEntityRole[];
  status: EditMemberStatus;
  joinedLabel?: string;
  lastActiveLabel?: string;
  isEditable?: boolean;
  /**
   * Per-entity override of `isEditable`. The tray lists every entity the current
   * user can see, but only entities they administer may be edited or submitted.
   */
  canEditEntity?: (legalEntityId: string) => boolean;
  onClose: () => void;
};

function allEntitiesEditable() {
  return true;
}

export function MultiLEEditTray({
  member,
  memberLegalEntityRoles,
  status,
  joinedLabel,
  lastActiveLabel,
  isEditable = true,
  canEditEntity = allEntitiesEditable,
  onClose,
}: MultiLEEditTrayProps) {
  const { formatMessage } = useIntl();
  const toast = useToast();
  const trayRef = useRef<TrayRefProps>(null);
  const [alertVisible, setAlertVisible] = useState(false);
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.organizationId ?? '';
  const refreshWorkspaceMembers = useRefreshWorkspaceMembers({ organizationId });
  const { supportedRoles, rolesByProductPerEntity, roleMap } = useRoles();

  const legalEntityIds = useMemo(
    () =>
      (selectedOrganization?.legalEntities ?? [])
        .map((le) => le.legalEntityId)
        .filter((id): id is string => Boolean(id)),
    [selectedOrganization?.legalEntities],
  );

  const localizedProducts = useMemo(
    () =>
      PRODUCTS.map(({ titleMessage, descriptionMessage, ...product }) => ({
        ...product,
        title: formatMessage(titleMessage),
        description: formatMessage(descriptionMessage),
      })),
    [formatMessage],
  );

  const supportedRolesSet = useMemo(() => new Set(supportedRoles), [supportedRoles]);

  const initialEntitySelections = useMemo(() => {
    const result: EntityIdToProductSelections = {};
    for (const ler of memberLegalEntityRoles ?? []) {
      if (ler.legalEntityId) {
        const productSelections: ProductSelections = {};
        for (const role of (ler.roles ?? []) as MemberRole[]) {
          if (supportedRolesSet.has(role)) {
            const key = getProductKey(role);
            if (key) {
              (productSelections[key] ??= []).push(role);
            }
          }
        }
        result[ler.legalEntityId] = productSelections;
      }
    }
    return result;
  }, [memberLegalEntityRoles, supportedRolesSet]);

  // Roles outside supportedRoles must be preserved when saving because
  // updateOrganizationMemberRole() replaces the full role set for each entity.
  const hiddenRolesByEntity = useMemo(() => {
    const result = new Map<string, MemberRole[]>();
    for (const ler of memberLegalEntityRoles ?? []) {
      if (ler.legalEntityId) {
        const hidden = ((ler.roles ?? []) as MemberRole[]).filter(
          (role) => !supportedRolesSet.has(role),
        );
        if (hidden.length > 0) {
          result.set(ler.legalEntityId, hidden);
        }
      }
    }
    return result;
  }, [memberLegalEntityRoles, supportedRolesSet]);

  // Per-entity, per-product roles the member holds that are no longer assignable
  // (e.g. off-flag coinbiz_admin). Surfaced in the picker as checked + disabled;
  // the assignable-only rolesByProductPerEntity omits them.
  const unassignableRolesByProductPerEntity = useMemo(() => {
    const result: Record<string, Record<string, MemberRoleMap[]>> = {};
    for (const ler of memberLegalEntityRoles ?? []) {
      if (ler.legalEntityId) {
        const byProduct: Record<string, MemberRoleMap[]> = {};
        for (const role of (ler.roles ?? []) as MemberRole[]) {
          const definition = roleMap[role];
          if (definition && !definition.assignable) {
            (byProduct[definition.productKey] ??= []).push(definition);
          }
        }
        result[ler.legalEntityId] = byProduct;
      }
    }
    return result;
  }, [memberLegalEntityRoles, roleMap]);

  // Held unassignable roles seed the selection, so they normally save via the
  // selection. But deselecting an entity clears its selection, so they're also
  // merged back on save to guarantee they're never dropped.
  const heldUnassignableRolesByEntity = useMemo(() => {
    const result = new Map<string, MemberRole[]>();
    for (const [entityId, byProduct] of Object.entries(unassignableRolesByProductPerEntity)) {
      const roles = Object.values(byProduct).flatMap((defs) => defs.map((def) => def.value));
      if (roles.length > 0) {
        result.set(entityId, roles);
      }
    }
    return result;
  }, [unassignableRolesByProductPerEntity]);

  const multiLEProductSelection = useMultiLEProductSelection({
    legalEntityIds,
    rolesByProductPerEntity,
    initialEntitySelections,
  });
  const { hasChanges, getLegalEntityRoles } = multiLEProductSelection;

  const isEntityEditable = useCallback(
    (legalEntityId: string) => isEditable && canEditEntity(legalEntityId),
    [canEditEntity, isEditable],
  );

  const readOnlyEntityIds = useMemo(
    () => legalEntityIds.filter((id) => !isEntityEditable(id)),
    [isEntityEditable, legalEntityIds],
  );

  const entities = useMultiLEProductAccessEntities({
    legalEntities: selectedOrganization?.legalEntities ?? [],
    localizedProducts,
    rolesByProductPerEntity,
    unassignableRolesByProductPerEntity,
    isEntityEditable,
    selection: multiLEProductSelection,
  });

  const name = [member.firstName, member.lastName].filter(Boolean).join(' ') || member.email;

  const legalEntityRoles = useMemo(() => {
    const rolesByEntity = new Map<string, MemberRole[]>();
    for (const { legalEntityId, roles } of getLegalEntityRoles()) {
      rolesByEntity.set(legalEntityId, [...roles]);
    }

    // Re-add held unassignable roles (deduped) so they survive even a full
    // entity deselect, which clears the selection they were seeded into.
    for (const [legalEntityId, held] of heldUnassignableRolesByEntity) {
      const existing = rolesByEntity.get(legalEntityId) ?? [];
      rolesByEntity.set(legalEntityId, [...new Set([...existing, ...held])]);
    }

    // Roles outside supportedRoles are preserved verbatim because
    // updateOrganizationMemberRole() replaces the full per-entity set.
    for (const [legalEntityId, hidden] of hiddenRolesByEntity) {
      const existing = rolesByEntity.get(legalEntityId) ?? [];
      rolesByEntity.set(legalEntityId, [...existing, ...hidden]);
    }

    return [...rolesByEntity.entries()].map(([legalEntityId, roles]) => ({ legalEntityId, roles }));
  }, [getLegalEntityRoles, heldUnassignableRolesByEntity, hiddenRolesByEntity]);

  const currentLegalEntityRoles = useMemo(
    () =>
      (memberLegalEntityRoles ?? [])
        .filter((ler): ler is typeof ler & { legalEntityId: string } => Boolean(ler.legalEntityId))
        .map((ler) => ({
          legalEntityId: ler.legalEntityId,
          roles: (ler.roles ?? []) as MemberRole[],
        })),
    [memberLegalEntityRoles],
  );

  const isGatedMutation = useMemo(
    () => isGatedRoleMutation(currentLegalEntityRoles, legalEntityRoles),
    [currentLegalEntityRoles, legalEntityRoles],
  );

  const { submit, stepUpModalProps, resetStepUpRecovery, handleStepUpProofTokenError } =
    useStepUpSubmission({
      isGatedRoleMutation: isGatedMutation,
    });

  const handleSaveSuccess = useCallback(() => {
    resetStepUpRecovery();
    toast.show(formatMessage(messages.saveSuccess));
    void refreshWorkspaceMembers();
    onClose();
  }, [formatMessage, onClose, refreshWorkspaceMembers, resetStepUpRecovery, toast]);

  const handleSaveFailure = useCallback(
    (error: unknown) => {
      if (handleStepUpProofTokenError(error)) {
        return;
      }
      toast.show(formatMessage(messages.saveError));
    },
    [formatMessage, handleStepUpProofTokenError, toast],
  );

  const { isSaving, saveEdit } = useEditSubmission({
    organizationId,
    memberUuid: member.userUuid,
    legalEntityRoles,
    readOnlyEntityIds,
    isFormValid: hasChanges,
    onSuccess: handleSaveSuccess,
    onFailure: handleSaveFailure,
  });

  const handleSave = useCallback(() => {
    submit(saveEdit);
  }, [saveEdit, submit]);

  const handleBeforeClose = useCallback(() => {
    if (hasChanges) {
      setAlertVisible(true);
      return;
    }

    trayRef.current?.close();
  }, [hasChanges]);

  const handleKeepEditing = useCallback(() => {
    setAlertVisible(false);
  }, []);

  const handleDiscard = useCallback(() => {
    setAlertVisible(false);
    trayRef.current?.close();
  }, []);

  const footer = useMemo(() => {
    if (!isEditable) {
      return null;
    }

    return (
      <SaveFooter visible={hasChanges || isSaving} onSave={handleSave} loading={isSaving}>
        {formatMessage(messages.saveButton)}
      </SaveFooter>
    );
  }, [formatMessage, handleSave, hasChanges, isEditable, isSaving]);

  const recentActivity = useMemo(
    () => <MemberRecentActivity organizationId={organizationId} memberUuid={member.userUuid} />,
    [member.userUuid, organizationId],
  );

  return (
    <>
      <EditMemberTray
        name={name}
        email={member.email}
        status={status}
        joinedLabel={joinedLabel}
        lastActiveLabel={lastActiveLabel}
        avatarSrc={member.avatarUrl}
        trayRef={trayRef}
        footer={footer}
        recentActivity={recentActivity}
        beforeClose={handleBeforeClose}
        onClose={onClose}
      >
        <MultiLEProductAccessGroup entities={entities} isEditable={isEditable} />
      </EditMemberTray>
      <StepUpTwoFactorModal {...stepUpModalProps} />
      <UnsavedChangesAlert
        variant="edit"
        visible={alertVisible}
        memberName={name}
        onKeepEditing={handleKeepEditing}
        onDiscard={handleDiscard}
      />
    </>
  );
}
